
import React from 'react';
import { AnalysisResult, COLORS } from '../types';
import { createTimelineData } from '../utils';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import CustomTooltip from '../CustomTooltip';

type SalesActivitiesTabProps = {
  analysisResult: AnalysisResult;
};

const SalesActivitiesTab = ({ analysisResult }: SalesActivitiesTabProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10 space-y-4">
        <h4 className="text-lg font-semibold text-primary mb-2">Recommended Activities</h4>
        <div className="space-y-3">
          {analysisResult.salesActivities?.map((activity, index) => (
            <div 
              key={index} 
              className="bg-secondary/50 rounded-lg p-4 border border-primary/10"
            >
              <h5 className="font-medium text-primary mb-2">{activity.activity}</h5>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-white/70">Expected Impact:</span>
                <span 
                  className={`px-2 py-1 rounded text-xs ${
                    activity.impact.toLowerCase().includes('high') 
                      ? 'bg-green-900/30 text-green-400' 
                      : activity.impact.toLowerCase().includes('medium')
                      ? 'bg-yellow-900/30 text-yellow-400'
                      : 'bg-blue-900/30 text-blue-400'
                  }`}
                >
                  {activity.impact}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-white/70">Timeline:</span>
                <span 
                  className={`px-2 py-1 rounded text-xs ${
                    activity.timeline.toLowerCase().includes('short') 
                      ? 'bg-purple-900/30 text-purple-400' 
                      : activity.timeline.toLowerCase().includes('medium')
                      ? 'bg-indigo-900/30 text-indigo-400'
                      : 'bg-blue-900/30 text-blue-400'
                  }`}
                >
                  {activity.timeline}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="bg-secondary/30 rounded-lg p-6 border border-primary/10">
        <h4 className="text-lg font-semibold text-primary mb-4">Activity Timeline</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={createTimelineData(analysisResult)}
              layout="vertical"
              margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis 
                type="number" 
                domain={[0, 100]} 
                stroke="#888" 
                tick={{ fill: '#fff', fontSize: 12 }}
              />
              <YAxis 
                dataKey="name" 
                type="category" 
                width={150}
                stroke="#888" 
                tick={{ fill: '#fff', fontSize: 12 }}
              />
              <Tooltip 
                content={<CustomTooltip />}
                formatter={(value) => [`${value} days`, 'Timeframe']}
                labelFormatter={(label) => [`Activity: ${label}`]}
              />
              <Bar 
                dataKey="value" 
                fill="#8884d8" 
                background={{ fill: '#3b3b5e' }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-center">
          <p className="text-xs text-gray-400">
            Timeline Scale: 0 = Immediate, 25 = Short-term (1-4 weeks), 50 = Medium-term (1-3 months), 75 = Long-term (3-6 months), 100 = Extended (6+ months)
          </p>
        </div>
      </div>
    </div>
  );
};

export default SalesActivitiesTab;
